import { useEffect, useState } from 'react';
import { Icon } from '../ui/Icon.js';

/**
 * Tells the user when the deployed build has moved on.
 *
 * An SPA fetches index.html exactly once. After that the tab holds whatever
 * bundle it started with, forever — and because the asset filenames are
 * content-hashed, the old bundle keeps loading perfectly from cache. The page
 * is not broken, it is simply the wrong version, which is worse: nothing looks
 * wrong, so nobody reloads.
 *
 * It never reloads on its own. A forced reload mid-form loses whatever was
 * being typed, and a stale page is a much smaller problem than that.
 */
const POLL_MS = 90_000;

export function UpdateBanner() {
  const [stale, setStale] = useState(false);

  useEffect(() => {
    if (stale) return;   // once known, stop asking
    let cancelled = false;

    const check = async (): Promise<void> => {
      try {
        // cache: 'no-store' matters: without it the browser can answer from
        // the same cache that is causing the problem.
        const res = await fetch('/version.json', { cache: 'no-store' });
        if (!res.ok) return;
        const body = (await res.json()) as { build?: string };
        if (!cancelled && body.build && body.build !== __ARC_BUILD__) setStale(true);
      } catch {
        // Offline, or the file is not deployed yet. Silence is correct: this
        // is a convenience, and a failed check must never surface as an error.
      }
    };

    const timer = setInterval(() => void check(), POLL_MS);
    // Checking on focus catches the common case -- a laptop opened the next
    // morning -- without waiting out the interval.
    const onFocus = (): void => { void check(); };
    window.addEventListener('focus', onFocus);
    void check();

    return () => {
      cancelled = true;
      clearInterval(timer);
      window.removeEventListener('focus', onFocus);
    };
  }, [stale]);

  if (!stale) return null;

  return (
    <div className="arc-update" role="status">
      <Icon name="info" size={14} />
      <span>A new version of ArcNet is available.</span>
      <button type="button" className="arc-btn arc-btn-sm"
        onClick={() => window.location.reload()}>
        Reload
      </button>
      <button type="button" className="arc-btn arc-btn-ghost arc-btn-icon"
        aria-label="Dismiss" onClick={() => setStale(false)}>
        <Icon name="close" size={13} />
      </button>
    </div>
  );
}
